import React, { useEffect, useMemo, useState } from "react";
import { Col, Input, Row } from "reactstrap";
import { useParams } from "react-router-dom";

import {
  foodPreOrderSiteLocationData,
  getSingleCustomerSiteLocationData,
  getSingleCustomerSiteLocationListData,
} from "../../../../store/actions";

//redux
import { useDispatch, useSelector } from "react-redux";

import "react-toastify/dist/ReactToastify.css";
import { isEmpty } from "lodash";

import LoadingOverlay from "../../../../Components/Common/LoadingOverlay/LoadingOverlay";
import InfoCard from "../../../../Components/Common/InfoCard/InfoCard";
import CreateEditCustomerSiteLocation from "../../../../Components/popUpComponents/CreateEditCustomerSiteLocation/CreateEditCustomerSiteLocation";
import SiteLocationCard from "./siteLocationCard";
import NoDataContent from "../../../../Components/Common/NoDataContent";

/**
 * Customer -> Site Locations (Task 24760).
 *
 * Brought onto the house pattern: the InfoCard shell used by the rest of the
 * customer page, a search box and one primary action in the header, and the
 * "..." row menu on each card (in siteLocationCard).
 *
 * Three things were also wrong rather than just unfashionable, and are fixed:
 *
 *  1. A red bin button sat at the top right and opened `deleteModalMulti` -
 *     a piece of state that was never rendered anywhere. Clicking it did
 *     nothing at all. There is no multi-select here, so it is gone rather
 *     than wired up.
 *  2. The card carried `id="invoiceList"`, copied from the invoices tab.
 *  3. The empty state read "No Results for: undefined" before the debounce
 *     had run once (see also Task 24753).
 */
const SingleCustomerSiteLocationList = ({ siteLocationData, page }) => {
  const dispatch = useDispatch();
  const params = useParams();
  const Customer_Id = params.id;

  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState([]);

  // Fixed list - it was state with no setter, which reads as "configurable"
  // and is not.
  const allowedSearchFilters = useMemo(
    () => [
      "BusinessName",
      "AddressCity",
      "FullName",
      "AddressPostCode",
      "AddressLine1",
      "SiteReference",
    ],
    []
  );

  const {
    singleCustomerSiteLocationListAPI,
    singleCustomerSiteLocationListAPILoading,
  } = useSelector((state) => ({
    singleCustomerSiteLocationListAPI:
      state.Customers.singleCustomerSiteLocationList?.customersitelocation_list,
    singleCustomerSiteLocationListAPILoading:
      state.Customers.singleCustomerSiteLocationListLoading,
  }));

  // UseEffects function Area
  useEffect(() => {
    if (!Customer_Id) return;
    dispatch(
      getSingleCustomerSiteLocationListData([
        { customer_data: { Id: Customer_Id } },
      ])
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Customer_Id]);

  // SEARCH FUNCTIONALITY STARTS HERE
  useEffect(() => {
    const source =
      page === "eventPreOrderLocations"
        ? siteLocationData
        : singleCustomerSiteLocationListAPI;

    const timer = setTimeout(() => {
      const term = (searchQuery || "").toLowerCase();
      setSearchResult(
        (source || []).filter((item) =>
          allowedSearchFilters.some((key) =>
            String(item?.[key] ?? "")
              .toLowerCase()
              .includes(term)
          )
        )
      );
    }, 300);
    return () => clearTimeout(timer);
  }, [
    searchQuery,
    singleCustomerSiteLocationListAPI,
    siteLocationData,
    page,
    allowedSearchFilters,
  ]);
  // SEARCH FUNCTIONALITY ENDS HERE

  const [modalSiteLocation, setModalSiteLocation] = useState(false);
  const [isEditSiteLocation, setIsEditSiteLocation] = useState(false);
  const toggleSiteLocation = () => {
    setModalSiteLocation(!modalSiteLocation);
  };

  const handleSiteSingleLocationGetData = (id) => {
    if (page === "eventPreOrderLocations") {
      dispatch(foodPreOrderSiteLocationData({ id: id }));
    } else {
      dispatch(getSingleCustomerSiteLocationData({ id: id }));
    }
  };

  const openCreate = () => {
    setIsEditSiteLocation(false);
    setModalSiteLocation(true);
  };

  // Count the list actually being shown. This component is also mounted by
  // Settings > Events with its own `siteLocationData`, where the customer
  // list is empty and a count off it would always read zero.
  const total = (
    (page === "eventPreOrderLocations"
      ? siteLocationData
      : singleCustomerSiteLocationListAPI) || []
  ).length;
  const hasNone = !singleCustomerSiteLocationListAPILoading && total === 0;

  const headerActions = (
    <div className="d-flex align-items-center gap-2">
      <div className="search-box">
        <Input
          type="search"
          className="form-control form-control-sm search"
          // Says what it will search, rather than the old
          // "{count} Search for..." which read as a result count.
          placeholder="Search sites..."
          onChange={(e) => setSearchQuery(e.target.value)}
          value={searchQuery}
        />
        <i className="ri-search-line search-icon"></i>
      </div>
      <button type="button" className="btn btn-sm btn-success" onClick={openCreate}>
        <i className="ri-add-line align-bottom me-1"></i>
        Add New Site
      </button>
    </div>
  );

  return (
    <React.Fragment>
      <div className="position-relative">
        <LoadingOverlay
          show={
            singleCustomerSiteLocationListAPILoading &&
            isEmpty(singleCustomerSiteLocationListAPI)
          }
          title="Loading site locations..."
        />

        {modalSiteLocation && (
          <CreateEditCustomerSiteLocation
            modal={modalSiteLocation}
            setModal={setModalSiteLocation}
            toggle={toggleSiteLocation}
            setIsEdit={setIsEditSiteLocation}
            isEdit={isEditSiteLocation}
            fromCustomerPage={true}
            Customer_Id={Customer_Id}
            page={page}
          />
        )}

        <Row>
          <Col lg={12}>
            <InfoCard
              title={total ? `Site Locations (${total})` : "Site Locations"}
              icon="ri-map-pin-2-line"
              accent="teal"
              actions={headerActions}
              className="customer-site-locations-card"
            >
              {!isEmpty(searchResult) ? (
                <SiteLocationCard
                  searchResult={searchResult}
                  isEditSiteLocation={isEditSiteLocation}
                  setIsEditSiteLocation={setIsEditSiteLocation}
                  setModalSiteLocation={setModalSiteLocation}
                  modalSiteLocation={modalSiteLocation}
                  toggleSiteLocation={toggleSiteLocation}
                  handleSiteSingleLocationGetData={
                    handleSiteSingleLocationGetData
                  }
                  fromCustomerPage={true}
                  Customer_Id={Customer_Id}
                  page={page}
                />
              ) : hasNone ? (
                <NoDataContent
                  titleText="Create your first Site Location"
                  paragraphText="Site locations are the addresses work actually happens at, separate from the customer's billing address."
                  useButton={true}
                  useClickCallBack={true}
                  callBackFunction={openCreate}
                  buttonText="Add First Site Location"
                />
              ) : (
                <NoDataContent
                  // Task 24753 all over again - an empty term must not be
                  // interpolated into the heading.
                  titleText={
                    searchQuery
                      ? `No sites matching "${searchQuery}"`
                      : "No site locations"
                  }
                  paragraphText="Try a different search, or add a new site location."
                  useButton={true}
                  useClickCallBack={true}
                  callBackFunction={openCreate}
                  buttonText="Add New Site Location"
                />
              )}
            </InfoCard>
          </Col>
        </Row>
      </div>
    </React.Fragment>
  );
};

export default SingleCustomerSiteLocationList;
